import { Form, Formik, FormikErrors } from "formik";
import { useState } from "react";
import * as yup from "yup";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";

import FormikErrorBanner from "src/components/form/FormikErrorBanner";
import FormikTextField from "src/components/form/FormikTextField";
import { addVanIntegrationVariables } from "src/graphql/generated/addVanIntegration";
import { getErrorMessage } from "src/util/errors";

import IntegrationTypeSelect from "./IntegrationTypeSelect";

type FormValuesAndErrors = addVanIntegrationVariables & {
  saveErrors?: any;
};

export type AddIntegrationValues = Omit<
  addVanIntegrationVariables,
  "subaccountId"
>;

export default function AddIntegrationDialog({
  open,
  onClose,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AddIntegrationValues) => Promise<void>;
}): JSX.Element {
  const [saveErrors, setSaveErrors] = useState<string | null>(null);

  const handleSubmit = async (payload: AddIntegrationValues) => {
    setSaveErrors(null);

    try {
      await onSubmit(payload);
    } catch (e) {
      setSaveErrors(getErrorMessage(e));
    }
  };

  return (
    <Dialog open={open}>
      <DialogTitle>New Integration</DialogTitle>
      <Formik
        initialValues={{
          name: "",
          applicationName: "",
          password: ""
        }}
        validationSchema={yup.object().shape({
          name: yup.string().required("Required"),
          applicationName: yup.string().required("Required"),
          password: yup.string().required("Required")
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <AddIntegrationForm
            onClose={onClose}
            isSubmitting={isSubmitting}
            saveErrors={saveErrors}
          />
        )}
      </Formik>
    </Dialog>
  );
}

function AddIntegrationForm({
  isSubmitting,
  onClose,
  saveErrors
}: {
  isSubmitting?: boolean;
  onClose: () => void;
  saveErrors: string | null;
}) {
  return (
    <Form>
      <DialogContent>
        <FormikErrorBanner
          renderBanner={(errors: FormikErrors<FormValuesAndErrors>) => {
            if (errors.saveErrors) {
              return `Error saving integration: ${errors.saveErrors}`;
            }
            return "Please fix the errors indicated below.";
          }}
          additionalErrors={saveErrors}
        />
        <IntegrationTypeSelect />
        <FormikTextField fullWidth name="name" label="Name this integration" />
        <FormikTextField
          fullWidth
          name="applicationName"
          label="Application name"
        />
        <FormikTextField fullWidth name="password" label="API key" />
      </DialogContent>

      <DialogActions>
        <Button disabled={isSubmitting} onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting}
          color="primary"
          variant="contained"
        >
          {isSubmitting ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Form>
  );
}
